// Session-wide memory of images that have actually DECODED, keyed by object
// PATH (everything before '?'). Presigned S3 URLs re-mint their querystring
// (stableImageUrl caps the churn at 10 min), so after a re-mint a tile remount
// sees a NEW url string for the SAME picture and used to flash a spinner while
// re-fetching. Same path ⇒ same bytes: recall the previously decoded url and
// keep painting it (browser cache hit) while the fresh url decodes and swaps
// in silently. TTL stays inside the presign expiry so a recalled url can still
// be re-fetched if the browser evicted it.
const PAINTED = new Map<string, { src: string; w: number; h: number; at: number }>();
const PAINTED_TTL_MS = 30 * 60 * 1000;

export const imagePathKey = (url: string, exactCapture = false): string => {
  const q = url.indexOf('?');
  const path = q < 0 ? url : url.slice(0, q);
  if (!exactCapture || q < 0) return path;
  const capture = new URLSearchParams(url.slice(q + 1)).get('t');
  return capture === null ? path : `${path}?t=${capture}`;
};

export function rememberPainted(src: string, w: number, h: number, exactCapture = false): void {
  PAINTED.set(imagePathKey(src, exactCapture), { src, w, h, at: Date.now() });
}

/** Last decoded url+dims for this src's object path, if still fresh. */
export function recallPainted(src: string, exactCapture = false): { src: string; w: number; h: number } | null {
  const hit = PAINTED.get(imagePathKey(src, exactCapture));
  return hit && Date.now() - hit.at < PAINTED_TTL_MS ? hit : null;
}

/** Synchronous browser-cache probe: dims if the EXACT src is already decoded. */
export function probeCachedImage(src: string): { w: number; h: number } | null {
  const img = new Image();
  img.src = src;
  return img.complete && img.naturalWidth
    ? { w: img.naturalWidth, h: img.naturalHeight || 1 }
    : null;
}
